import * as Sentry from '@sentry/vue'
import type { RouteLocationNormalized } from 'vue-router'

import axiosPlugin from '@/lib/axios'
import { AdminAuthService } from '@/services/admin-auth'
import type { IAdminSession, IAdminTokens } from '@/types'

const ADMIN_SESSION_KEY = 'adminSession'
// Refresh slightly before the real expiry to absorb network latency and clock skew.
const EXPIRY_SKEW_SECONDS = 30

export function getStoredAdminSession(): IAdminSession | null {
  const raw = localStorage.getItem(ADMIN_SESSION_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as IAdminSession
  } catch {
    return null
  }
}

export function persistAdminSession(tokens: IAdminTokens): IAdminSession {
  const session: IAdminSession = {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: Math.floor(Date.now() / 1000) + tokens.expiresIn,
  }
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session))
  return session
}

export function clearAdminSession() {
  localStorage.removeItem(ADMIN_SESSION_KEY)
}

/** Persists a fresh token pair and keeps axiosPlugin's default header in sync with it. */
export function applyAdminSession(tokens: IAdminTokens): IAdminSession {
  const session = persistAdminSession(tokens)
  axiosPlugin.defaults.headers.common.Authorization = `Bearer ${session.accessToken}`
  return session
}

/** Tears down the session everywhere it's cached: storage and the axios default header. */
export function discardAdminSession(): void {
  clearAdminSession()
  delete axiosPlugin.defaults.headers.common.Authorization
}

function isExpired(expiresAt: number): boolean {
  return Math.floor(Date.now() / 1000) >= expiresAt - EXPIRY_SKEW_SECONDS
}

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(payload))
  } catch (err) {
    console.error('Failed to decode admin token', err)
    return null
  }
}

// Single-flight guard: the refresh token is single-use (the backend rotates it and treats reuse as
// compromise, revoking the whole session family), so concurrent callers must share one refresh
// request instead of each posting the same token.
let refreshInFlight: Promise<string | null> | null = null

async function refreshSession(refreshToken: string): Promise<string | null> {
  try {
    const tokens = await AdminAuthService.refresh(refreshToken)
    const next = applyAdminSession(tokens)
    return next.accessToken
  } catch (err) {
    console.error('Admin token refresh failed', err)
    discardAdminSession()
    return null
  }
}

/**
 * Returns a valid admin access token, rotating it through /auth/admin/refresh when the current one
 * is (about to be) expired. Concurrent calls share a single refresh request. Returns null — without
 * redirecting — when there is no usable session, so callers can decide how to react.
 */
export async function resolveAdminAccessToken(): Promise<string | null> {
  const session = getStoredAdminSession()
  if (!session) return null

  if (!isExpired(session.expiresAt)) {
    axiosPlugin.defaults.headers.common.Authorization = `Bearer ${session.accessToken}`
    return session.accessToken
  }

  if (!refreshInFlight) {
    refreshInFlight = refreshSession(session.refreshToken).finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

/**
 * Used by the axios request interceptor: resolves a valid access token, or redirects to the login
 * page and throws so the pending request is aborted instead of firing with a stale header.
 */
export async function checkAndUpdateAuthToken(): Promise<string> {
  const token = await resolveAdminAccessToken()
  if (!token) {
    window.location.href = '/login'
    throw new Error('Admin session expired')
  }
  return token
}

/**
 * Global router guard (registered once in router/index.ts) rather than a per-route `beforeEnter`:
 * Vue Router only re-runs `beforeEnter` when a record newly enters the matched chain, so a
 * per-record guard on `/admin` would never re-check role on in-app navigation between its
 * children. Gating on `to.meta.requiresRole` makes this safe to run on every navigation, admin or
 * not — every admin route declares it, no other route does.
 */
export async function adminAuthGuard(to: RouteLocationNormalized) {
  if (!to.meta.requiresRole) return

  const token = await resolveAdminAccessToken()
  if (!token) {
    console.info('Not authenticated, redirecting to login')
    return { name: 'login' }
  }

  const payload = decodeJwt(token)
  if (!payload) {
    discardAdminSession()
    return { name: 'login' }
  }

  Sentry.setUser({
    id: String(payload.sub ?? ''),
    email: payload.email as string | undefined,
  })

  // superadmin can reach any route; an admin can only reach admin-level routes. Any other role
  // has no admin session to speak of — drop it and send them to login (redirecting them to the
  // dashboard would loop, since the dashboard itself requires the admin role).
  const requiredRole = to.meta.requiresRole as string
  const role = payload.role as string | undefined
  if (role !== requiredRole && role !== 'superadmin') {
    console.warn(`Access denied: ${role} → ${to.path}`)
    if (role === 'admin') {
      return { name: 'dashboard' }
    }
    discardAdminSession()
    return { name: 'login', query: { error: 'unauthorized' } }
  }
}
